import React, { useEffect, useMemo, useRef, useState } from "react";
import SnGrid from "./SnGrid";
import { ColumnConfig, SnGridRef } from "./SnGridTypes";
import { global } from "@shared/libs/Global";

/**
 * 26.11.04 kdy 그리드 데이터 인스펙터.
 * 그리드 상단 탭 제목을 Ctrl+Shift+더블클릭 하면 뜬다 (SnGrid.openDataInspector).
 * 상단: 현재 그리드에 걸린 컬럼 필터 조건을 "필터 조건 [ ... ]" 한 줄로.
 * 하단: 전체/표시중/추가/수정/삭제 행과 컬럼정의를 SnGrid 로 그대로 보여준다 (숨김 컬럼 포함).
 */

interface Props {
  datasets: Record<string, any[]>;
  columnConfig: ColumnConfig[];
}

const btnStyle = (active: boolean): React.CSSProperties => ({
  padding: "2px 10px",
  border: "1px solid rgb(186,191,199)",
  borderRadius: "4px",
  background: active ? "rgb(11, 12, 105)" : "#fff",
  color: active ? "#fff" : "#333",
  cursor: "pointer",
  fontSize: "12px",
  whiteSpace: "nowrap",
});

/** 인스펙터에서 원본 컬럼 타입을 유지할 것들. 그 외는 코드값이 그대로 보이게 SnTextBox 로 바꾼다 */
const RAW_KEEP_TYPES = new Set(["SnNumberBox", "SnDateBox", "SnDateMonthBox"]);

/** ag-grid 필터 모델 한 건을 사람이 읽을 문장으로 */
const describeFilter = (model: any): string => {
  if (model == null) return "";
  if (Array.isArray(model.conditions)) {
    return model.conditions
      .map(describeFilter)
      .join(` ${model.operator ?? "AND"} `);
  }
  if (model.filterType === "set" || Array.isArray(model.values)) {
    const v = model.values ?? [];
    const head = v.slice(0, 10).map(String).join(", ");
    return `in [${head}${v.length > 10 ? `, …+${v.length - 10}` : ""}]`;
  }
  const type = model.type ?? "";
  if (type === "blank") return "blank";
  if (type === "notBlank") return "notBlank";
  if (type === "inRange") return `${model.filter} ~ ${model.filterTo}`;
  return `${type} ${model.filter ?? model.dateFrom ?? ""}`.trim();
};

/**
 * 컬럼 필터 모델을 팝업 타이틀에 붙일 한 줄로. 필드명만 쓴다. 예) [no_line equals 1, cd_wh in [A, B]]
 * 필터가 없으면 빈 문자열.
 */
export const formatFilterModel = (
  filterModel: Record<string, any> | null | undefined
): string => {
  const fm = filterModel ?? {};
  const keys = Object.keys(fm);
  if (keys.length === 0) return "";
  return `[${keys.map((k) => `${k} ${describeFilter(fm[k])}`).join(", ")}]`;
};

/** 셀에 그대로 못 뿌리는 값(객체/함수)은 문자열로 */
const flattenRow = (r: any) => {
  const out: Record<string, any> = {};
  if (!r || typeof r !== "object") return out;
  Object.keys(r).forEach((k) => {
    const v = r[k];
    if (typeof v === "function") out[k] = "fn";
    else if (v !== null && typeof v === "object") out[k] = JSON.stringify(v);
    else out[k] = v;
  });
  return out;
};

/** 행들의 키 합집합. 기준 컬럼정의 순서를 우선하고 나머지는 발견 순서 */
const collectKeys = (rows: any[], base: ColumnConfig[]) => {
  const found = new Set<string>();
  rows.forEach((r) => Object.keys(r).forEach((k) => found.add(k)));
  const ordered: string[] = [];
  base.forEach((c) => {
    if (found.has(c.cd_column)) {
      ordered.push(c.cd_column);
      found.delete(c.cd_column);
    }
  });
  found.forEach((k) => ordered.push(k));
  return ordered;
};

export default function SnGridDataInspector({
  datasets,
  columnConfig,
}: Props) {
  const { userInfo } = global();
  const keys = useMemo(() => Object.keys(datasets), [datasets]);
  const [selected, setSelected] = useState<string>(keys[0] ?? "");
  const gridRef = useRef<SnGridRef>(null);

  // 헤더 글자가 잘리지 않게 헤더+셀 내용 기준으로 폭 자동 조정 (skipHeader=false)
  const autoSize = () => {
    requestAnimationFrame(() => gridRef.current?.autoSizeAllColumns(false));
  };
  // 데이터셋을 바꾸면 SnGrid 가 key 로 새로 마운트되므로 컬럼 준비 완료 뒤 한 번 더
  useEffect(() => {
    gridRef.current?.whenColumnsReady().then(autoSize);
  }, [selected]);

  const nameMap = useMemo(() => {
    const m = new Map<string, ColumnConfig>();
    columnConfig.forEach((c) => m.set(c.cd_column, c));
    return m;
  }, [columnConfig]);

  const isConfigView = selected === "컬럼정의";

  // 표시할 행 (원본을 건드리지 않게 복사)
  const rows = useMemo(() => {
    const src = datasets[selected] ?? [];
    return src.map(flattenRow);
  }, [datasets, selected]);

  // 표시용 컬럼정의. 데이터 행이면 원본 정의를 전부 보이게 바꾸고, 정의에 없는 키는 텍스트 컬럼으로 추가
  const viewConfig = useMemo<ColumnConfig[]>(() => {
    const base: ColumnConfig = {
      cd_firm: userInfo?.cd_firm ?? "",
      cd_menu: "", // 컨텍스트메뉴/항목설정 조회가 실제 메뉴를 건드리지 않게 비움
      cd_grid: "",
      cd_user: userInfo?.cd_user ?? "",
      cd_column: "",
      nm_column: "",
      tp_column: "SnTextBox",
      fg_visible: "Y",
      no_seq: 0,
    };
    const orderedKeys = collectKeys(rows, isConfigView ? [] : columnConfig);
    return orderedKeys.map((k, i) => {
      const cfg = !isConfigView ? nameMap.get(k) : undefined;
      if (cfg) {
        // 숫자/날짜 포맷만 유지하고 나머지(콤보·코드·체크 등)는 텍스트로 바꿔 저장된 코드값 그대로 보이게 한다
        const keepType = RAW_KEEP_TYPES.has(cfg.tp_column);
        return {
          ...cfg,
          cd_menu: "",
          cd_grid: "",
          nm_column:
            !cfg.nm_column || cfg.nm_column === k
              ? k
              : `${cfg.nm_column} (${k})`,
          tp_column: keepType ? cfg.tp_column : "SnTextBox",
          js_combo: null,
          cd_displaycolumn: null,
          cd_clas: null,
          tpColumnByRow: undefined,
          valueFormatter: keepType ? cfg.valueFormatter : undefined,
          fg_visible: "Y",
          yn_required: "N",
          cd_popup: null,
          popup_apply: undefined,
          dc_editcon: null,
          no_seq: i + 1,
        };
      }
      return { ...base, cd_column: k, nm_column: k, no_seq: i + 1 };
    });
  }, [rows, columnConfig, nameMap, isConfigView, userInfo]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        gap: "6px",
        fontSize: "12px",
      }}
    >
      {/* 데이터셋 선택 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          flexWrap: "wrap",
          flex: "0 0 auto",
        }}
      >
        {keys.map((k) => (
          <button
            key={k}
            type="button"
            style={btnStyle(k === selected)}
            onClick={() => setSelected(k)}
          >
            {k} ({datasets[k]?.length ?? 0})
          </button>
        ))}
      </div>

      {/* 그리드: 데이터셋이 바뀌면 컬럼정의도 달라지므로 key 로 새로 마운트 */}
      <div style={{ flex: "1 1 auto", minHeight: "300px", width: "100%" }}>
        <SnGrid
          key={selected}
          ref={gridRef}
          columnConfig={viewConfig}
          rowData={rows}
          events={{ onRowDataUpdated: autoSize }}
          etcProps={{
            isEditable: false,
            rowSelection: "single",
            showFooter: false,
            enableInitialSort: false,
            disableColumnSetting: true,
            suppressInitialCellFocus: true,
            suppressColumnVirtualisation: true, // 화면 밖 컬럼도 헤더 기준 자동 폭이 먹게

          }}
        />
      </div>
    </div>
  );
}
